import { useEffect, useState } from "react";
import axios from "axios";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import "./PlanActualReport.css";
//import ActivityPlanShiftReport from "./ActivityPlanShiftReport";
import config from "../../../config/config";

const API_BASE_URL = config.API;// =============================================================
// AUTH TOKEN
// =============================================================
const getAuthToken = () => {
    try {
        const user = JSON.parse(localStorage.getItem("user"));
        return user?.access_token || "";
    } catch {
        return "";
    }
};

// =============================================================
// DATE FORMAT
// =============================================================
const formatDate = (date) => {
    if (!date) return "";

    const d = new Date(date);

    if (Number.isNaN(d.getTime())) {
        return date;
    }

    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();

    return `${day}.${month}.${year}`;
};

// =============================================================
// MONTH NAME
// =============================================================
const getMonthName = (date) => {
    if (!date) return "";

    const d = new Date(date);

    if (Number.isNaN(d.getTime())) {
        return "";
    }

    return d.toLocaleString("en-US", {
        month: "long",
    }).toUpperCase();
};

// =============================================================
// VALUE HANDLER
// =============================================================
const valueOrBlank = (value) => {
    return value === null || value === undefined ? "" : value;
};

// =============================================================
// NUMBER HANDLER
// =============================================================
const numberOrZero = (value) => {
    if (value === null || value === undefined || value === "") {
        return 0;
    }

    const number = Number(value);

    return Number.isNaN(number) ? 0 : number;
};

// =============================================================
// PLAN ACTUAL REPORT
// =============================================================
const PlanActualReport = () => {
    const [reportDate, setReportDate] = useState("");
    const [report, setReport] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    // =============================================================
    // FETCH REPORT
    // =============================================================
    const fetchReport = async () => {
        if (!reportDate) {
            setError("Please select a report date.");
            return;
        }

        try {
            setLoading(true);
            setError("");

            const token = getAuthToken();

            const response = await axios.get(
                `${API_BASE_URL}/planactual/getPlanAndActual`,
                {
                    params: {
                        date: reportDate,
                    },
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            if (response.data?.success) {
                setReport(response.data.data);
                //console.log("FULL API RESPONSE:", response.data);
                console.log("ShiftA manpower:", response.data.data.shiftA.manpower);
                console.log("ShiftB manpower:", response.data.data.shiftB.manpower);

            } else {
                setReport(null);

                setError(
                    response.data?.message ||
                    "Unable to load report."
                );
            }
        } catch (err) {
            console.error("Plan Actual API Error:", err);

            setReport(null);

            setError(
                err.response?.data?.message ||
                "Failed to load report."
            );
        } finally {
            setLoading(false);
        }
    };

    // =============================================================
    // INITIAL LOAD
    // =============================================================
    useEffect(() => {
        const today = new Date();

        const yyyy = today.getFullYear();

        const mm = String(
            today.getMonth() + 1
        ).padStart(2, "0");

        const dd = String(
            today.getDate()
        ).padStart(2, "0");

        setReportDate(
            `${yyyy}-${mm}-${dd}`
        );
    }, []);

    // =============================================================
    // EXPORT PDF
    // =============================================================
    const exportPDF = async () => {
        const element =
            document.getElementById(
                "plan-actual-report"
            );

        if (!element) {
            return;
        }

        try {
            setLoading(true);

            const canvas = await html2canvas(
                element,
                {
                    scale: 3,
                    useCORS: true,
                    backgroundColor: "#ffffff",
                    logging: false,
                }
            );

            const imgData =
                canvas.toDataURL(
                    "image/png",
                    1.0
                );

            const pdf = new jsPDF({
                orientation: "landscape",
                unit: "mm",
                format: "a4",
            });

            const pageWidth = 297;
            const pageHeight = 210;
            const margin = 2;

            const maxWidth =
                pageWidth - margin * 2;

            const maxHeight =
                pageHeight - margin * 2;

            const ratio =
                canvas.width /
                canvas.height;

            let width = maxWidth;

            let height =
                width / ratio;

            if (height > maxHeight) {
                height = maxHeight;

                width =
                    height * ratio;
            }

            const x =
                (pageWidth - width) / 2;

            const y =
                (pageHeight - height) / 2;

            pdf.addImage(
                imgData,
                "PNG",
                x,
                y,
                width,
                height
            );

            pdf.save(
                `PlanProduction_&_Actual_Production_${reportDate}.pdf`
            );
        } catch (err) {
            console.error(
                "PDF generation failed:",
                err
            );
        } finally {
            setLoading(false);
        }
    };

    // =============================================================
    // LOADING
    // =============================================================
    if (loading && !report) {
        return (
            <div className="report-loading">
                Loading report...
            </div>
        );
    }

    // =============================================================
    // DATA
    // =============================================================
    const header =
        report?.header?.[0] || {};

    const shiftA =
        report?.shiftA || {};

    const shiftB =
        report?.shiftB || {};

    // IMPORTANT:
    // Manpower summary is returned inside shiftB.manpower[0]
    const manpower =
        report?.shiftB?.manpower?.[0] || {};

    const users =
        report?.users?.[0] || {};

    const shiftAItems =
        shiftA.items || [];

    const shiftBItems =
        shiftB.items || [];

    const shiftAActivities =
        shiftA.activities || [];

    const shiftBActivities =
        shiftB.activities || [];


    // =============================================================
    // HEADER DATA
    // =============================================================
    const planDate =
        header.PlanDate ||
        reportDate;

    const displayDate =
        formatDate(planDate);

    const monthName =
        getMonthName(planDate);


    // =============================================================
    // SHIFT NAMES
    // =============================================================
    const shiftAName =
        shiftA.shift?.[0]?.Shift ||
        "SHIFT-A";

    const shiftBName =
        shiftB.shift?.[0]?.Shift ||
        "SHIFT-B";


    // =============================================================
    // PROCESS TOTALS
    // =============================================================

    // Planned Process Manpower from API
    const shiftAProcessPlan =
        numberOrZero(
            manpower.ShiftA_Process_Manpower
        );

    const shiftBProcessPlan =
        numberOrZero(
            manpower.ShiftB_Process_Manpower
        );


    // Actual Process Manpower
    const shiftAProcessActual =
        shiftAActivities.reduce(
            (total, activity) =>
                total +
                numberOrZero(
                    activity.Actual_Man_Power
                ),
            0
        );

    const shiftBProcessActual =
        shiftBActivities.reduce(
            (total, activity) =>
                total +
                numberOrZero(
                    activity.Actual_Man_Power
                ),
            0
        );


    // =============================================================
    // PROCESS MANPOWER FROM ACTIVITIES
    // =============================================================
    const shiftAProcessManpower =
        shiftAActivities.reduce(
            (total, item) =>
                total +
                numberOrZero(item?.Man_Power),
            0
        );

    const shiftBProcessManpower =
        shiftBActivities.reduce(
            (total, item) =>
                total +
                numberOrZero(item?.Man_Power),
            0
        );


    // =============================================================
    // OPERATORS
    // =============================================================
    const shiftAOperators =
        shiftAItems.reduce(
            (total, item) =>
                total + numberOrZero(item?.Operators),
            0
        );

    const shiftBOperators =
        shiftBItems.reduce(
            (total, item) =>
                total + numberOrZero(item?.Operators),
            0
        );


    // =============================================================
    // TOTAL MANPOWER
    // =============================================================

    // Shift A = Operators + Process Manpower
    const shiftATotalManpower =
        shiftAOperators +
        shiftAProcessManpower;


    // Shift B = Operators + Process Manpower
    const shiftBTotalManpower =
        shiftBOperators +
        shiftBProcessManpower;


    // Shift A + Shift B
    const totalManpower =
        shiftATotalManpower +
        shiftBTotalManpower;


    // =============================================================
    // MAX ROWS
    // =============================================================
    const productionRows =
        Math.max(
            shiftAItems.length,
            shiftBItems.length
        );

    const processRows =
        Math.max(
            shiftAActivities.length,
            shiftBActivities.length
        );

    // =============================================================
    // DEBUGGING

    // =============================================================
    // RENDER
    // =============================================================
    return (
        <div className="plan-actual-page">

            {/* =====================================================
                TOOLBAR
            ====================================================== */}
            <div className="report-toolbar">

                <div className="date-search">

                    <label>
                        Select Production Date
                    </label>

                    <input
                        type="date"
                        value={reportDate}
                        onChange={(e) =>
                            setReportDate(
                                e.target.value
                            )
                        }
                    />
                    <button
                        className="btn btn-success"
                        onClick={fetchReport}
                        disabled={loading}
                    >
                        {loading
                            ? "Loading..."
                            : "Search"}
                    </button>
                </div>
                <button
                    className="btn-pdf"
                    onClick={exportPDF}
                    disabled={!report || loading}
                >
                    {loading
                        ? "Generating..."
                        : "Export PDF"}
                </button>

            </div>

            {/* =====================================================
                ERROR
            ====================================================== */}
            {error && (
                <div className="report-error">
                    {error}
                </div>
            )}

            {/* =====================================================
                REPORT
            ====================================================== */}
            {report && (

                <div
                    id="plan-actual-report"
                    className="report-container"
                >

                    {/* =================================================
                        COMPANY
                    ================================================= */}
                    <div className="company-name">
                        {header.Company ||
                            "PRAG INDUSTRIES INDIA PVT LTD Unit I"}
                    </div>

                    {/* =================================================
                        TITLE
                    ================================================= */}
                    <div className="report-title">
                        PRODUCTION PLAN & ACTUAL PRODUCTION 
                        <span>
                            FOR THE MONTH OF {monthName}
                            {" "} - DT - {displayDate}
                        </span>
                    </div>

                    {/* =================================================
                        MAIN 16 COLUMN TABLE
                    ================================================= */}
                    <table className="excel-report-table">

                        <colgroup>

                            {/* SHIFT A */}
                            <col className="col-item" />
                            <col className="col-machine" />
                            <col className="col-cavity" />
                            <col className="col-operator" />
                            <col className="col-plan" />
                            <col className="col-actual" />
                            <col className="col-rejected" />
                            <col className="col-remark" />

                            {/* SHIFT B */}
                            <col className="col-item" />
                            <col className="col-machine" />
                            <col className="col-cavity" />
                            <col className="col-operator" />
                            <col className="col-plan" />
                            <col className="col-actual" />
                            <col className="col-rejected" />
                            <col className="col-remark" />

                        </colgroup>

                        <tbody>

                            {/* =================================================
                                SHIFT HEADERS
                            ================================================= */}
                            <tr className="shift-header-row">

                                <td
                                    colSpan="8"
                                    className="shift-a"
                                >
                                    {shiftAName}
                                </td>

                                <td
                                    colSpan="8"
                                    className="shift-b"
                                >
                                    {shiftBName}
                                </td>

                            </tr>

                            {/* =================================================
                                PRODUCTION HEADERS
                            ================================================= */}
                            <tr className="table-header-row">

                                {/* SHIFT A */}
                                <th>Item</th>
                                <th>Machine</th>
                                <th>Cavity</th>
                                <th>Operator</th>
                                <th>Plan 12hrs</th>
                                <th>Actual</th>
                                <th>Rejected</th>
                                <th>Remark</th>

                                {/* SHIFT B */}
                                <th>Item</th>
                                <th>Machine</th>
                                <th>Cavity</th>
                                <th>Operator</th>
                                <th>Plan 12hrs</th>
                                <th>Actual</th>
                                <th>Rejected</th>
                                <th>Remark</th>

                            </tr>

                            {/* =================================================
                                PRODUCTION ROWS
                            ================================================= */}
                            {productionRows > 0 ? (

                                Array.from({
                                    length: productionRows
                                }).map((_, index) => {

                                    const a =
                                        shiftAItems[index] ||
                                        {};

                                    const b =
                                        shiftBItems[index] ||
                                        {};

                                    return (
                                        <tr
                                            key={`production-${index}`}
                                        >

                                            {/* SHIFT A */}

                                            <td className="text-cell">
                                                {valueOrBlank(
                                                    a.Item
                                                )}
                                            </td>

                                            <td className="text-cell">
                                                {valueOrBlank(
                                                    a.Machine_No
                                                )}
                                            </td>

                                            <td className="center">
                                                {valueOrBlank(
                                                    a.No_Of_Cavity
                                                )}
                                            </td>

                                            <td className="center">
                                                {valueOrBlank(
                                                    a.Operators
                                                )}
                                            </td>

                                            <td className="center">
                                                {valueOrBlank(
                                                    a.Plan
                                                )}
                                            </td>

                                            <td className="center">
                                                {valueOrBlank(
                                                    a.Actual
                                                )}
                                            </td>

                                            <td className="center">
                                                {a.Rejected ===
                                                    null ||
                                                    a.Rejected ===
                                                    undefined
                                                    ? ""
                                                    : numberOrZero(
                                                        a.Rejected
                                                    )}
                                            </td>

                                            <td className="text-cell">
                                                {valueOrBlank(
                                                    a.Remark
                                                )}
                                            </td>

                                            {/* SHIFT B */}

                                            <td className="text-cell">
                                                {valueOrBlank(
                                                    b.Item
                                                )}
                                            </td>

                                            <td className="text-cell">
                                                {valueOrBlank(
                                                    b.Machine_No
                                                )}
                                            </td>

                                            <td className="center">
                                                {valueOrBlank(
                                                    b.No_Of_Cavity
                                                )}
                                            </td>

                                            <td className="center">
                                                {valueOrBlank(
                                                    b.Operators
                                                )}
                                            </td>

                                            <td className="center">
                                                {valueOrBlank(
                                                    b.Plan
                                                )}
                                            </td>

                                            <td className="center">
                                                {valueOrBlank(
                                                    b.Actual
                                                )}
                                            </td>

                                            <td className="center">
                                                {b.Rejected ===
                                                    null ||
                                                    b.Rejected ===
                                                    undefined
                                                    ? ""
                                                    : numberOrZero(
                                                        b.Rejected
                                                    )}
                                            </td>

                                            <td className="text-cell">
                                                {valueOrBlank(
                                                    b.Remark
                                                )}
                                            </td>

                                        </tr>
                                    );
                                })

                            ) : (

                                <tr>

                                    <td
                                        colSpan="16"
                                        className="no-data"
                                    >
                                        No production records
                                    </td>

                                </tr>

                            )}

                            {/* =================================================
                                PROCESS SHIFT HEADER
                            ================================================= */}
                            <tr className="process-shift-row">

                                <td
                                    colSpan="8"
                                    className="shift-a"
                                >
                                    {shiftAName}
                                </td>

                                <td
                                    colSpan="8"
                                    className="shift-b"
                                >
                                    {shiftBName}
                                </td>

                            </tr>

                            {/* =================================================
                                PROCESS HEADERS
                            ================================================= */}
                            <tr className="table-header-row process-header">

                                {/* SHIFT A */}
                                <th style={{ textAlign: "left" }}>Process/Activities</th>
                                <th>Plan Man Power</th>
                                <th>Actual Man Power</th>
                                <th>Remark</th>

                                <th></th>
                                <th></th>
                                <th></th>
                                <th></th>
                                {/* SHIFT B */}
                                <th style={{ textAlign: "left" }}>Process/Activities</th>
                                <th>Plan Man Power</th>
                                <th>Actual Man Power</th>
                                <th>Remark</th>
                                <th></th>
                                <th></th>
                                <th></th>
                                <th></th>

                            </tr>

                            {/* =================================================
                                PROCESS ROWS
                            ================================================= */}
                            {processRows > 0 ? (

                                Array.from({
                                    length: processRows
                                }).map((_, index) => {

                                    const a =
                                        shiftAActivities[index] ||
                                        {};

                                    const b =
                                        shiftBActivities[index] ||
                                        {};

                                    return (
                                        <tr
                                            key={`process-${index}`}
                                        >

                                            {/* SHIFT A PROCESS */}

                                            <td className="process-name">
                                                {valueOrBlank(
                                                    a.Process_Name
                                                )}
                                            </td>

                                            <td className="center">
                                                {valueOrBlank(
                                                    a.Man_Power
                                                )}
                                            </td>

                                            <td className="center">
                                                {valueOrBlank(
                                                    a.Actual_Man_Power
                                                )}
                                            </td>

                                            <td className="text-cell">
                                                {valueOrBlank(
                                                    a.Remark
                                                )}
                                            </td>

                                            <td></td>
                                            <td></td>
                                            <td></td>
                                            <td></td>

                                            {/* SHIFT B PROCESS */}

                                            <td className="process-name">
                                                {valueOrBlank(
                                                    b.Process_Name
                                                )}
                                            </td>

                                            <td className="center">
                                                {valueOrBlank(
                                                    b.Man_Power
                                                )}
                                            </td>

                                            <td className="center">
                                                {valueOrBlank(
                                                    b.Actual_Man_Power
                                                )}
                                            </td>

                                            <td className="text-cell">
                                                {valueOrBlank(
                                                    b.Remark
                                                )}
                                            </td>

                                            <td></td>
                                            <td></td>
                                            <td></td>
                                            <td></td>

                                        </tr>
                                    );
                                })

                            ) : (

                                <tr>

                                    <td
                                        colSpan="16"
                                        className="no-data"
                                    >
                                        No process records
                                    </td>

                                </tr>

                            )}

                            {/* =================================================
                                PROCESS TOTALS
                            ================================================= */}
                            <tr className="total-row">

                                {/* SHIFT A */}
                                <td colSpan="1">
                                    TOTAL {shiftAName}
                                </td>

                                <td className="center">
                                    {shiftAProcessPlan}
                                </td>

                                <td className="center">
                                    {shiftAProcessActual}
                                </td>

                                <td>
                                    -
                                </td>

                                <td colSpan="4"></td>

                                {/* SHIFT B */}
                                <td>
                                    TOTAL {shiftBName}
                                </td>

                                <td className="center">
                                    {shiftBProcessPlan}
                                </td>

                                <td className="center">
                                    {shiftBProcessActual}
                                </td>

                                <td>
                                    -
                                </td>

                                <td colSpan="4"></td>

                            </tr>


                            {/* =================================================
                                    MANPOWER TOTALS
                             ================================================= */}

                            <tr className="manpower-title-row">

                                <td
                                    colSpan="8"
                                    className="summary-heading"
                                >
                                    TOTAL {shiftAName}
                                </td>

                                <td
                                    colSpan="8"
                                    className="summary-heading"
                                >
                                    TOTAL {shiftBName}
                                </td>

                            </tr>


                            {/* =================================================
    OPERATORS
================================================= */}

                            <tr>

                                {/* SHIFT A OPERATORS */}
                                <td colSpan="6">
                                    Operators
                                </td>

                                <td
                                    colSpan="2"
                                    className="center"
                                >
                                    {shiftAOperators}
                                </td>


                                {/* SHIFT B OPERATORS */}
                                <td colSpan="6">
                                    Operators
                                </td>

                                <td
                                    colSpan="2"
                                    className="center"
                                >
                                    {shiftBOperators}
                                </td>

                            </tr>


                            {/* =================================================
                                PROCESS MANPOWER
                            ================================================= */}

                            <tr>

                                {/* SHIFT A PROCESS MANPOWER */}
                                <td colSpan="6">
                                    Process Manpower
                                </td>

                                <td
                                    colSpan="2"
                                    className="center"
                                >
                                    {shiftAProcessManpower}
                                </td>


                                {/* SHIFT B PROCESS MANPOWER */}
                                <td colSpan="6">
                                    Process Manpower
                                </td>

                                <td
                                    colSpan="2"
                                    className="center"
                                >
                                    {shiftBProcessManpower}
                                </td>

                            </tr>

                            {/* =================================================
    SHIFT TOTAL MANPOWER
================================================= */}

                            <tr
                                className="grand-total-row"
                                style={{
                                    // backgroundColor: "skyblue",
                                    // color: "red"
                                }}
                            >

                                {/* SHIFT A TOTAL */}
                                <td colSpan="6">
                                    Total Manpower
                                </td>

                                <td
                                    colSpan="2"
                                    className="center"
                                >
                                    {shiftATotalManpower}
                                </td>


                                {/* SHIFT B TOTAL */}
                                <td colSpan="6">
                                    Total Manpower
                                </td>

                                <td
                                    colSpan="2"
                                    className="center"
                                >
                                    {shiftBTotalManpower}
                                </td>

                            </tr>


                            {/* =================================================
    GRAND TOTAL
================================================= */}

                            <tr
                                style={{
                                    backgroundColor: "skyblue",
                                    color: "red"
                                }}
                            >

                                <td colSpan="15">
                                    Grand Total {shiftAName} + {shiftBName}
                                </td>

                                <td className="center">
                                    {totalManpower}
                                </td>

                            </tr>


                            {/* =================================================
                                VERIFICATION
                            ================================================= */}
                            <tr className="verification-row">

                                <td colSpan="8">
                                    <strong>
                                        Verified by :
                                    </strong>{" "}
                                    {users.created_by || ""}
                                </td>

                                <td colSpan="8">
                                    <strong>
                                        Verified by :
                                    </strong>{" "}
                                    {users.approved_by || ""}
                                </td>

                            </tr>

                        </tbody>

                    </table>

                </div>

            )}
         

  
   

        </div>

       
    );
};

export default PlanActualReport;

